import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { Mail, ArrowLeft, Send, Sparkles } from 'lucide-react';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast.error('Por favor, informe seu email');
      return;
    }

    // Validação básica de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Por favor, informe um email válido');
      return;
    }

    setIsLoading(true);

    try {
      await api.post('/auth/forgot-password', { email });
      setEmailSent(true);
      toast.success('Email enviado! Verifique sua caixa de entrada.');
    } catch (error: any) {
      console.error('Erro ao solicitar recuperação:', error);
      // Sempre mostrar mensagem genérica por segurança
      toast.success('Se o email existir, você receberá instruções para recuperação');
      setEmailSent(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 to-primary-800 flex flex-col">
      {/* Header with link to landing page */}
      <div className="w-full bg-white/10 backdrop-blur-sm border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity w-fit">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-primary-600" />
            </div>
            <span className="text-2xl font-bold text-white">
              Roda de Prêmios
            </span>
          </Link>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Back to login link */}
          <Link 
            to="/admin/login" 
            className="flex items-center gap-2 text-primary-100 hover:text-white transition-colors text-sm w-fit"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para o login
          </Link>

          {/* Header */}
          <div>
          <div className="mx-auto h-12 w-12 bg-white rounded-full flex items-center justify-center">
            <Mail className="h-6 w-6 text-primary-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            Esqueceu sua senha?
          </h2>
          <p className="mt-2 text-center text-sm text-primary-100">
            {emailSent
              ? 'Verifique seu email para continuar'
              : 'Informe seu email para receber instruções de recuperação'}
          </p>
        </div>

        {emailSent ? (
          /* Success State */
          <div className="mt-8 space-y-6">
            <div className="rounded-lg bg-white p-6 shadow-xl">
              <div className="text-center space-y-4">
                <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
                  <Send className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">
                  Email enviado com sucesso!
                </h3>
                <p className="text-sm text-gray-600">
                  Se existe uma conta cadastrada com o email <strong>{email}</strong>,
                  você receberá instruções para redefinir sua senha.
                </p>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        <strong>Importante:</strong> Verifique também sua caixa de spam.
                        O link expira em 1 hora.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center space-y-3">
              <button
                onClick={() => {
                  setEmailSent(false);
                  setEmail('');
                }}
                className="text-sm text-white hover:text-primary-200 underline"
              >
                Enviar para outro email
              </button>
              <div>
                <Link
                  to="/login"
                  className="inline-flex items-center text-sm font-medium text-white hover:text-primary-200"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Voltar para login
                </Link>
              </div>
            </div>
          </div>
        ) : (
          /* Form State */
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md shadow-sm">
              <div>
                <label htmlFor="email" className="sr-only">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="Digite seu email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="space-y-3">
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Send className="h-5 w-5 mr-2" />
                    Enviar link de recuperação
                  </>
                )}
              </button>

              <Link
                to="/login"
                className="group relative w-full flex justify-center py-2 px-4 border border-white text-sm font-medium rounded-md text-white hover:bg-white hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Voltar para login
              </Link>
            </div>

            <div className="text-center">
              <p className="text-xs text-primary-100">
                Lembrou sua senha?{' '}
                <Link
                  to="/login"
                  className="font-medium text-white hover:text-primary-200 underline"
                >
                  Fazer login
                </Link>
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
    </div>
  );
};

export default ForgotPasswordPage;
